import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@src/components/ui/table'
import { Skeleton } from '@src/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@src/components/ui/select'
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Fragment, useMemo, useState } from 'react'
import { getRiskProfileAssetsColumns } from '../constants/RiskProfileAssetsTable.columns'
import type { RiskProfileAsset } from '../types/riskProfileAssets.types'
import { useLayerLookup } from '../hooks/useLayerLookup'
import { useSelectedWorkspaceFromUrl } from '../hooks/useSelectedWorkspaceFromUrl'
import { useRiskFilters } from '../../shared/hooks/useRiskFilters'

interface RiskProfileAssetsTableProps {
  assets: RiskProfileAsset[]
  isLoading?: boolean
}

const LIMIT_OPTIONS = [10, 25, 100] as const
type LimitOption = (typeof LIMIT_OPTIONS)[number]

export function RiskProfileAssetsTable({ assets, isLoading }: RiskProfileAssetsTableProps) {
  const { getTitle } = useLayerLookup()
  const [limit, setLimit] = useState<LimitOption>(10)
  const { workspaceId } = useSelectedWorkspaceFromUrl()
  const { hazardGroups } = useRiskFilters()

  const displayedAssets = useMemo(() => assets.slice(0, limit), [assets, limit])

  const showRank = useMemo(() => assets.some((asset) => asset.asset_rank !== null), [assets])

  const columns = useMemo(
    () => getRiskProfileAssetsColumns(getTitle, workspaceId, hazardGroups, showRank),
    [getTitle, workspaceId, hazardGroups, showRank],
  )

  const table = useReactTable({
    data: displayedAssets,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => Object.keys(row.original.identifying_information).length > 0,
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }

  if (!assets.length) {
    return (
      <div className="rounded-md border border-border p-8 text-center text-muted-foreground">
        No asset data available for the selected filters.
      </div>
    )
  }

  return (
    <div>
      <div className="border rounded-lg">
        <Table className="border-spacing-0 border-collapse rounded-lg overflow-hidden">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="bg-sidebar-accent">
                {hg.headers.map((header) => (
                  <TableHead key={header.id} className="font-semibold">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <Fragment key={row.id}>
                <TableRow>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() && (
                  <TableRow key={`${row.id}-details`} className="bg-gray-50 hover:bg-gray-50">
                    <TableCell />
                    <TableCell colSpan={columns.length - 1}>
                      <div className="py-2 px-4">
                        <div className="text-sm font-semibold mb-2 text-gray-700">
                          Identifying Information
                        </div>
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                          {Object.entries(row.original.identifying_information).map(
                            ([key, value]) => (
                              <div key={key} className="flex gap-2">
                                <dt className="font-medium text-gray-600">{key}:</dt>
                                <dd className="text-gray-900">
                                  {value != null ? String(value) : '—'}
                                </dd>
                              </div>
                            ),
                          )}
                        </dl>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center gap-2 pt-3">
        <Select
          value={limit.toString()}
          onValueChange={(val) => setLimit(Number(val) as LimitOption)}
        >
          <SelectTrigger className="h-10 w-[180px] rounded-lg bg-background text-sm shadow-sm border border-border">
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="start">
            {LIMIT_OPTIONS.map((option) => (
              <SelectItem key={option} value={option.toString()} className="text-foreground">
                Show Top {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}
